/**
 * Typed wrapper around GET /api/v1/directory.
 *
 * Path + shape taken verbatim from backend/internal/router (the
 * authMW-only, NOT feature-gated, NOT CSRF-gated GET) and
 * backend/internal/handlers/directory.go.
 *
 * This endpoint is reachable by ANY authenticated tenant user (a plain
 * employee, a line manager — not just HR), so it is the directory the
 * 1:1 picker and the People page consume. It returns ONLY non-PII
 * roster fields. A platform user with no tenant context gets 403 —
 * callers degrade to an id label, never invent a name.
 */

import { api } from "./client";
import type { ListDirectoryResponse } from "@/types/directory";

export const DIRECTORY_BASE = "/api/v1/directory";

// GET /api/v1/directory — active users only by default. Pass
// includeInactive to also list deactivated users (id→name resolution on
// historical rows where the actor may since be deactivated).
// Pass page + perPage to paginate; omit both for the full roster
// (backwards-compat; existing hooks that do id→name resolution always
// want the full list).
// Pass `q` (name/role/team substring) and/or `department` (exact match) to
// filter SERVER-SIDE so the People search box + department dropdown span the
// whole roster, not just the loaded page (bug B-A6). When paginating, the
// response also carries the tenant-wide DISTINCT `departments` list.
export async function listDirectory(
  opts?: {
    includeInactive?: boolean;
    page?: number;
    perPage?: number;
    q?: string;
    department?: string;
    /** Only the caller's direct + indirect reports (Manage → My Team). */
    reportsOfMe?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListDirectoryResponse> {
  const params: Record<string, string> = {};
  if (opts?.includeInactive) params.include_inactive = "true";
  if (opts?.page != null && opts.page > 0) params.page = String(opts.page);
  if (opts?.perPage != null && opts.perPage > 0) params.per_page = String(opts.perPage);
  const q = opts?.q?.trim();
  if (q) params.q = q;
  const department = opts?.department?.trim();
  if (department) params.department = department;
  if (opts?.reportsOfMe) params.reports_of = "me";
  const { data } = await api.get<ListDirectoryResponse>(DIRECTORY_BASE, {
    params: Object.keys(params).length > 0 ? params : undefined,
    signal,
  });
  return data;
}

// PUT /api/v1/directory/settings — HR only (wave B): whether colleagues see
// each other's work email / phone in the employee directory.
export async function updateDirectorySettings(body: {
  show_email?: boolean;
  show_phone?: boolean;
}): Promise<{ email: boolean; phone: boolean }> {
  const { data } = await api.put<{ email: boolean; phone: boolean }>(
    `${DIRECTORY_BASE}/settings`,
    body,
  );
  return data;
}
